import React, { useState } from "react";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import { Textarea } from "../../../design/textarea";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../../../design/fieldset";
import { useAttorney } from "../../../contexts/AttorneyContext";
import { Attorney } from "@visitproof/types";
import PhoneNumberInput from "../../../components/PhoneNumberInput";

interface Props {
  attorney: Attorney;
}

interface Errors {
  firstName?: string;
  lastName?: string;
  email?: string;
}

const EditAttorneyButton: React.FC<Props> = ({ attorney }) => {
  const { updateAttorney } = useAttorney();
  const [isOpen, setIsOpen] = useState(false);
  const [firstName, setFirstName] = useState(attorney.firstName);
  const [lastName, setLastName] = useState(attorney.lastName);
  const [suffix, setSuffix] = useState(attorney.suffix || "");
  const [email, setEmail] = useState(attorney.email || "");
  const [phoneNumber, setPhoneNumber] = useState(
    attorney.phoneNumber?.replace("+", "") || ""
  );
  const [mobilePhone, setMobilePhone] = useState(
    attorney.mobilePhone?.replace("+", "") || ""
  );
  const [website, setWebsite] = useState(attorney.website || "");
  const [firmName, setFirmName] = useState(attorney.firmName || "");
  const [barNumber, setBarNumber] = useState(attorney.barNumber || "");
  const [notes, setNotes] = useState(attorney.notes || "");
  // Legal Aid fields
  const [legalAidName, setLegalAidName] = useState(attorney.legalAidName || "");
  const [legalAidEmail, setLegalAidEmail] = useState(attorney.legalAidEmail || "");
  const [legalAidPhone, setLegalAidPhone] = useState(
    attorney.legalAidPhone?.replace("+", "") || ""
  );
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const handleOpen = () => {
    setFirstName(attorney.firstName);
    setLastName(attorney.lastName);
    setSuffix(attorney.suffix || "");
    setEmail(attorney.email || "");
    setPhoneNumber(attorney.phoneNumber?.replace("+", "") || "");
    setMobilePhone(attorney.mobilePhone?.replace("+", "") || "");
    setWebsite(attorney.website || "");
    setFirmName(attorney.firmName || "");
    setBarNumber(attorney.barNumber || "");
    setNotes(attorney.notes || "");
    setLegalAidName(attorney.legalAidName || "");
    setLegalAidEmail(attorney.legalAidEmail || "");
    setLegalAidPhone(attorney.legalAidPhone?.replace("+", "") || "");
    setErrors({});
    setIsOpen(true);
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      const cleanPhoneNumber = phoneNumber
        ? `+${phoneNumber.replace(/\D/g, "")}`
        : undefined;
      const cleanMobilePhone = mobilePhone
        ? `+${mobilePhone.replace(/\D/g, "")}`
        : undefined;
      const cleanLegalAidPhone = legalAidPhone
        ? `+${legalAidPhone.replace(/\D/g, "")}`
        : undefined;
      await updateAttorney(attorney.id, {
        firstName,
        lastName,
        suffix: suffix || undefined,
        phoneNumber: cleanPhoneNumber,
        mobilePhone: cleanMobilePhone,
        website: website || undefined,
        email: email || undefined,
        firmName: firmName || undefined,
        barNumber: barNumber || undefined,
        notes: notes || undefined,
        legalAidName: legalAidName || undefined,
        legalAidEmail: legalAidEmail || undefined,
        legalAidPhone: cleanLegalAidPhone,
      });
      setIsOpen(false);
    } catch (error) {
      console.error("Error updating attorney:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const validate = () => {
    const newErrors: Errors = {};
    if (!firstName) newErrors.firstName = "First Name is required";
    if (!lastName) newErrors.lastName = "Last Name is required";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Invalid email address";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <>
      <Button type="button" outline onClick={handleOpen}>
        Edit
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Edit Attorney</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-3 sm:gap-4">
                <Field>
                  <Label>First Name</Label>
                  <Input
                    type="text"
                    value={firstName}
                    placeholder="First Name"
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  {errors.firstName && (
                    <ErrorMessage>{errors.firstName}</ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Last Name</Label>
                  <Input
                    type="text"
                    value={lastName}
                    placeholder="Last Name"
                    onChange={(e) => setLastName(e.target.value)}
                  />
                  {errors.lastName && (
                    <ErrorMessage>{errors.lastName}</ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Suffix</Label>
                  <Input
                    type="text"
                    value={suffix}
                    placeholder="Esq."
                    onChange={(e) => setSuffix(e.target.value)}
                  />
                </Field>
              </div>
              <Field>
                <Label>Firm Name</Label>
                <Input
                  type="text"
                  value={firmName}
                  placeholder="Law Firm Name"
                  onChange={(e) => setFirmName(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={email}
                  placeholder="attorney@example.com"
                  onChange={(e) => setEmail(e.target.value)}
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </Field>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                <Field>
                  <Label>Office Phone</Label>
                  <PhoneNumberInput
                    defaultValue={phoneNumber}
                    value={phoneNumber}
                    placeholder="Office Phone"
                    onChange={setPhoneNumber}
                  />
                </Field>
                <Field>
                  <Label>Mobile Phone</Label>
                  <PhoneNumberInput
                    defaultValue={mobilePhone}
                    value={mobilePhone}
                    placeholder="Mobile Phone"
                    onChange={setMobilePhone}
                  />
                </Field>
              </div>
              <Field>
                <Label>Website</Label>
                <Input
                  type="url"
                  value={website}
                  placeholder="https://lawfirm.com"
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Bar Number</Label>
                <Input
                  type="text"
                  value={barNumber}
                  placeholder="State Bar Number"
                  onChange={(e) => setBarNumber(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Notes</Label>
                <Textarea
                  value={notes}
                  placeholder="Additional notes about this attorney..."
                  rows={3}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Field>

              {/* Legal Aid / Assistant Section */}
              <div className="pt-4 border-t border-zinc-200 dark:border-zinc-700">
                <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-4">
                  Legal Aid / Assistant
                </p>
                <div className="space-y-4">
                  <Field>
                    <Label>Name</Label>
                    <Input
                      type="text"
                      value={legalAidName}
                      placeholder="Legal Aid Name"
                      onChange={(e) => setLegalAidName(e.target.value)}
                    />
                  </Field>
                  <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                    <Field>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={legalAidEmail}
                        placeholder="legalaid@example.com"
                        onChange={(e) => setLegalAidEmail(e.target.value)}
                      />
                    </Field>
                    <Field>
                      <Label>Phone</Label>
                      <PhoneNumberInput
                        defaultValue={legalAidPhone}
                        value={legalAidPhone}
                        placeholder="Phone Number"
                        onChange={setLegalAidPhone}
                      />
                    </Field>
                  </div>
                </div>
              </div>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditAttorneyButton;
